<template lang="pug">
.google-business-page-setup-flow
  v-overlay.round-xl.mb-2(absolute :value="true" opacity="0.8")
    v-progress-circular(indeterminate color="white" size="50" width="5")
  review-add-site-dialog(v-if="openReviewAddSiteDialog" :open.sync="openReviewAddSiteDialog" @next="step = 'checkLocation'" @cancel="cancelAction" @connectOrChange="step = 'connectGoogleAuth'")
  review-selection-dialog(v-if="openReviewSelectionDialog" :open.sync="openReviewSelectionDialog" :connectionType="selectionType" :items="selectionItems" @next="selectedItem = $event; step = 'confirmSelection'" @cancel="cancelAction")
  review-confirm-selection-dialog(v-if="openReviewConfirmSelectionDialog" :open.sync="openReviewConfirmSelectionDialog" :connectionType="selectionType" :selectedItem="selectedItem" @next="step = 'postConfirmSelection'" @back="step = 'changeSelection'" @cancel="cancelAction")
  review-add-google-review-link-dialog(v-if="openGoogleReviewLinkDialog" :open.sync="openGoogleReviewLinkDialog" @back="step = 'checkAccount'" @next="step = 'finish'" @cancel="cancelAction")
  review-error-dialog(v-if="openReviewErrorDialog" :open.sync="openReviewErrorDialog" :errorDetails="errorDetails" @cancel="cancelAction")
</template>

<script>
import { mapGetters } from 'vuex'
import ReviewAddSiteDialog from '@/components/review/dialogs/AddSiteDialog.vue'
import ReviewSelectionDialog from '@/components/review/dialogs/SelectionDialog.vue'
import ReviewConfirmSelectionDialog from '@/components/review/dialogs/ConfirmSelectionDialog.vue'
import ReviewAddGoogleReviewLinkDialog from '@/components/review/dialogs/AddGoogleReviewLinkDialog.vue'
import ReviewErrorDialog from '@/components/review/dialogs/ErrorDialog.vue'

export default {
  name: 'GoogleBusinessPageSetupFlow',
  components: {
    ReviewAddSiteDialog,
    ReviewSelectionDialog,
    ReviewConfirmSelectionDialog,
    ReviewAddGoogleReviewLinkDialog,
    ReviewErrorDialog
  },
  props: {
    redirectQueryParams: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isFetching: false,
      step: 'mounted',
      selectionType: null,
      selectionItems: [],
      openReviewAddSiteDialog: false,
      openReviewSelectionDialog: false,
      openReviewConfirmSelectionDialog: false,
      openGoogleReviewLinkDialog: false,
      openReviewErrorDialog: false,
      errorDetails: null,
      selectedItem: null
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      reviewAccount: 'review/getMainAccount',
      gmbAccounts: 'review/getGmbAccounts',
      gmbLocations: 'review/getGmbLocations'
    }),
    currentBaseDomain() {
      // if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      //   return `http://${window.location.host}${window.location.pathname}`
      // }
      return `https://${this.company.slug}.${process.env.VUE_APP_BASE_DOMAIN}${window.location.pathname}`
    },
    redirectUrl() {
      return this.redirectQueryParams ? `${this.currentBaseDomain}${this.redirectQueryParams}` : this.currentBaseDomain
    },
    googleAuthUrl() {
      return `${process.env.VUE_APP_BASE_URL}/reviews/${this.company.slug}/google_auth?redirect_url=${this.redirectUrl}`
    }
  },
  watch: {
    async step(value) {
      this.resetAllDialogs()
      switch (value) {
        case 'connectGoogleAuth':
          await this.connectGoogleAuthAction()
          break

        case 'checkAccount':
          await this.checkAccountAction()
          break

        case 'checkLocation':
          await this.checkLocationAction()
          break

        case 'reviewSite':
          this.openReviewAddSiteDialog = true
          break

        case 'selectAccount':
          this.selectionType = 'account'
          this.selectionItems = this.gmbAccounts
          this.openReviewSelectionDialog = true
          break

        case 'selectLocation':
          this.selectionType = 'location'
          this.selectionItems = this.gmbLocations
          this.openReviewSelectionDialog = true
          break

        case 'confirmSelection':
          this.openReviewConfirmSelectionDialog = true
          break

        case 'changeSelection':
          this.selectedItem = null
          this.openReviewSelectionDialog = true
          break

        case 'postConfirmSelection':
          await this.postConfirmSelectionAction()
          break
        
        case 'googleReviewLink':
          this.openGoogleReviewLinkDialog = true
          break

        case 'finish':
          window.location.reload()
          break

        case 'error':
          this.resetAllDialogs()
          this.openReviewErrorDialog = true
          break

        default:
          break
      }
    },
  },
  async mounted() {
    const valid = this.checkRoute()
    if (!valid) {
      return
    }
    const allFetching = [this.fetchMainAccount(), this.fetchGmbAccount()]
    try {
      this.isFetching = true
      await Promise.all([...allFetching])
      this.step = 'checkAccount'
    } catch (error) {
      this.constructErrorDetails(error.message)
      this.step = 'error'
      return
    }
    this.isFetching = false
  },
  methods: {
    resetRouteQuery() {
      this.$router.replace({'query': null});
    },
    checkRoute() {
      if (this.$route.query?.success === 'false') {
        this.step = 'GoogleError'
        this.constructErrorDetails('Unable to complete google authentication process')
        this.step = 'error'
        this.resetRouteQuery()
        return false
      }
      this.resetRouteQuery()
      return true
    },
    fetchMainAccount() {
      return this.$axios.get(`reviews/${this.company.slug}/google`)
        .then(({ data }) => {
          this.$store.commit('review/setMainAccount', data)
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            return
          }
          throw error
        })
    },
    fetchGmbAccount() {
      return this.$axios.get(`reviews/${this.company.slug}/gmb_accounts`)
        .then(({ data }) => {
          this.$store.commit('review/setGmbAccounts', data)
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            return
          }
          this.constructErrorDetails(error.message)
          this.step = 'error'
          throw error
        })
    },
    fetchGmbLocation() {
      const params =  {
        account_id: this.reviewAccount.account_id
      }
      return this.$axios.get(`reviews/${this.company.slug}/gmb_locations`, { params })
        .then(({ data }) => {
          this.$store.commit('review/setGmbLocations', data)
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            return
          }
          throw error
        })
    },
    validateGmbLocations() {
      if (this.gmbLocations.length === 0) {
        this.constructErrorDetails('Not location found for selected GMB account. Please change GMB acount')
        this.step = 'error'
        return false
      }
      return true
    },
    async checkAccountAction() {
      if (!this.reviewAccount) {
        this.step = 'reviewSite'
        return
      }
      // Check if has multiple gmb account
      if (this.reviewAccount && !this.reviewAccount.account_id && this.gmbAccounts.length > 1) {
        this.step = 'selectAccount'
        return
      }
      // Check if only 1 gmb account, auto append to database
      if (this.reviewAccount && !this.reviewAccount.account_id && this.gmbAccounts.length === 1) {
        try {
          await this.updateMainAccount(this.gmbAccounts[0])
          this.step = 'reviewSite'
          return
        } catch (error) {
          this.constructErrorDetails(error.message)
          this.step = 'error'
          return
        }
      }
      // Everything else
      this.step = 'reviewSite'
    },
    async checkLocationAction() {
      try {
        // Check location for selected account
        await this.fetchGmbLocation()
        const valid = this.validateGmbLocations()
        if (!valid) {
          return
        }
      } catch (error) {
        this.constructErrorDetails(error.message)
        this.step = 'error'
        return
      }
      // Check if has multiple gmb locations
      if (this.gmbLocations.length > 1) {
        this.step = 'selectLocation'
        return
      }
      // Check if only 1 gmb location, auto append to database
      try {
        await this.updateMainAccount(this.gmbLocations[0])
        this.step = 'googleReviewLink'
        return
      } catch (error) {
        this.constructErrorDetails(error.message)
        this.step = 'error'
        return
      }
    },
    async connectGoogleAuthAction() {
      try {
        window.location.href = this.googleAuthUrl
      } catch (error) {
        this.constructErrorDetails(error.message)
        this.step = 'error'
        return
      }
    },
    async postConfirmSelectionAction() {
      try {
        await this.updateMainAccount(this.selectedItem)
      } catch (error) {
        this.constructErrorDetails(error.message)
        this.step = 'error'
        return
      }
      this.selectedItem = null
      if (this.selectionType === 'account') {
        this.step = 'checkLocation'
        this.selectionType = null
        this.openReviewConfirmSelectionDialog = false
      } else {
        this.step = 'googleReviewLink'
        this.selectionType = null
        this.openReviewConfirmSelectionDialog = false
      }
    },
    updateMainAccount(value) {
      return this.$axios.patch(`reviews/${this.company.slug}/google`, value)
        .then(({ data }) => {
          this.$store.commit('review/setMainAccount', data)
          return true
        })
    },
    resetAllDialogs() {
      this.isFetching = false,
      this.openReviewAddSiteDialog = false,
      this.openReviewSelectionDialog = false,
      this.openReviewConfirmSelectionDialog = false,
      this.openGoogleReviewLinkDialog = false
    },
    constructErrorDetails(message) {
      this.errorDetails = {
        step: this.step,
        message: message
      }
    },
    cancelAction() {
      this.$emit('cancel')
    }
  },
}
</script>

<style scoped>
</style>
